const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseHour = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return null;
  const parsed = Number(text.includes(':') ? text.split(':')[0] : text);
  if (!Number.isFinite(parsed)) return null;
  const hour = parsed === 24 ? 0 : Math.trunc(parsed);
  return hour >= 0 && hour <= 23 ? hour : null;
};

const parseTimeBlock = (block) => {
  const parts = String(block || '').split('-').map((part) => part.trim());
  if (parts.length !== 2) return null;
  const start = parseHour(parts[0]);
  const end = parseHour(parts[1]);
  if (start === null || end === null) return null;
  const hours = [];
  let hour = start;
  do {
    hours.push(hour);
    hour = (hour + 1) % 24;
  } while (hour !== end && hours.length < 24);
  return { start, end, hours };
};

export const buildContiguousTodBuckets = (policy) => {
  const details = Array.isArray(policy?.slotDetails) ? policy.slotDetails : [];
  const buckets = [];
  details.forEach((detail) => {
    const name = String(detail?.displayName || detail?.name || 'TOD').trim();
    (detail?.timeBlocks || []).forEach((timeBlock) => {
      const parsed = parseTimeBlock(timeBlock);
      if (!parsed || !parsed.hours.length) return;
      const normalizedBlock = `${String(timeBlock).split('-')[0].trim()}-${String(timeBlock).split('-')[1].trim()}`;
      buckets.push({
        key: `${String(detail?.name || name).trim()}|${normalizedBlock}`,
        name,
        timeBlock: normalizedBlock,
        label: `${name} (${normalizedBlock})`,
        hours: parsed.hours,
        start: parsed.start,
        end: parsed.end,
        overnight: parsed.start > parsed.end,
      });
    });
  });
  return buckets.sort((a, b) => {
    if (a.overnight !== b.overnight) return a.overnight ? -1 : 1;
    return a.start - b.start;
  });
};

const sumRowsForBucket = (rows, bucket, valueGetter) => (
  (Array.isArray(rows) ? rows : []).reduce((total, row) => {
    const hour = parseHour(row?.Time ?? row?.time);
    if (hour === null || !bucket.hours.includes(hour)) return total;
    return total + toNumber(valueGetter(row));
  }, 0)
);

const consumptionValue = (row) => (
  row?.['Hourly Consumption (kWh)']
  ?? row?.Consumption
  ?? row?.consumption
  ?? 0
);

const demandMetValue = (row) => {
  const explicit = row?.DemandMet ?? row?.['Demand Met'] ?? row?.['Net Settled Units'];
  if (explicit !== undefined && explicit !== null && explicit !== '') return explicit;
  return toNumber(row?.replacement)
    + toNumber(row?.intra_tod_settlement_amt)
    + toNumber(row?.inter_tod_settlement_amt)
    + toNumber(row?.['Total Banking settlement']);
};

export const buildBatteryTodRows = ({
  buckets = [],
  consumptionRows = [],
  bankingRows = [],
  batteryRows = [],
  tariffs = {},
} = {}) => {
  const baseRows = buckets.map((bucket) => ({
    bucket,
    consumption: sumRowsForBucket(consumptionRows, bucket, consumptionValue),
  }));
  const hasTimedBankingRows = (Array.isArray(bankingRows) ? bankingRows : []).some(
    (row) => parseHour(row?.Time ?? row?.time) !== null,
  );
  const demandMetByName = {};
  if (!hasTimedBankingRows) {
    (Array.isArray(bankingRows) ? bankingRows : []).forEach((row) => {
      const name = String(row?.Slot ?? row?.slot ?? '').trim().toLowerCase();
      if (!name || name === 'total') return;
      demandMetByName[name] = (demandMetByName[name] || 0) + toNumber(demandMetValue(row));
    });
  }
  const consumptionByName = baseRows.reduce((totals, item) => {
    const name = item.bucket.name.toLowerCase();
    totals[name] = (totals[name] || 0) + item.consumption;
    return totals;
  }, {});

  return baseRows.map(({ bucket, consumption }) => {
  const timedDemandMet = sumRowsForBucket(bankingRows, bucket, demandMetValue);
  const nameKey = bucket.name.toLowerCase();
  const allocatedDemandMet = hasTimedBankingRows
    ? timedDemandMet
    : (
      consumptionByName[nameKey] > 0
        ? toNumber(demandMetByName[nameKey]) * consumption / consumptionByName[nameKey]
        : 0
    );
  const demandMet = Math.min(consumption, Math.max(allocatedDemandMet, 0));
  const gridConsumption = Math.max(consumption - demandMet, 0);
  const batteryDischargeUnits = Math.max(sumRowsForBucket(
    batteryRows,
    bucket,
    (row) => row?.['Battery Discharge unit considering inefficiency'] ?? 0,
  ), 0);
  const rawTariff = tariffs?.[bucket.key];
  const hasTariff = rawTariff !== null && rawTariff !== undefined && String(rawTariff).trim() !== ''
    && Number.isFinite(Number(rawTariff));
  const tariff = hasTariff ? Number(rawTariff) : null;
  const gridCostWithBattery = hasTariff ? gridConsumption * tariff : null;
  const gridCostWithoutBattery = hasTariff
    ? (gridConsumption + batteryDischargeUnits) * tariff
    : null;
  return {
    ...bucket,
    consumption,
    demandMet,
    gridConsumption,
    batteryDischargeUnits,
    tariff,
    hasTariff,
    savings: hasTariff ? batteryDischargeUnits * tariff : null,
    gridCostWithBattery,
    gridCostWithoutBattery,
  };
  });
};

export const summarizeBatteryTodRows = (rows = []) => rows.reduce((totals, row) => ({
  consumption: totals.consumption + toNumber(row.consumption),
  demandMet: totals.demandMet + toNumber(row.demandMet),
  gridConsumption: totals.gridConsumption + toNumber(row.gridConsumption),
  batteryDischargeUnits: totals.batteryDischargeUnits + toNumber(row.batteryDischargeUnits),
  savings: totals.savings + toNumber(row.savings),
  gridCostWithBattery: totals.gridCostWithBattery + toNumber(row.gridCostWithBattery),
  gridCostWithoutBattery: totals.gridCostWithoutBattery + toNumber(row.gridCostWithoutBattery),
}), {
  consumption: 0,
  demandMet: 0,
  gridConsumption: 0,
  batteryDischargeUnits: 0,
  savings: 0,
  gridCostWithBattery: 0,
  gridCostWithoutBattery: 0,
});
